import { DeepPartial } from '@apollo/client/utilities';
import { cloneDeep, merge } from 'lodash';
import {
  ApplicationLicense,
  Machine,
  ScratchpadBucket,
} from '../../../../../api/graphql/generated';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { URModel } from '../../../../../robot/manufacturers/universal-robots/ur.types';
import { logger } from '../../../../../util/logger';
import {
  ApplicationDefaultParameterSetData,
  ApplicationDefaultParameterSetTagToEquipmentMapping,
  Flow,
  FlowItem,
  FlowItemParameterRef,
  Tool,
} from '../../../shared/model/flow.types';
import { AbstractFlowService } from '../../../shared/service/abstract-flow.service';
import {
  FlowItemService,
  FlowRuleService,
} from '../../../shared/service/service.types';
import { BeaconWeldingData } from '../model/beacon-welding-data';

class WeldingFlowServiceImpl extends AbstractFlowService<
  BeaconWeldingData.FlowConfig,
  typeof BeaconWeldingData.FlowSectionType
> {
  constructor(
    private license: ApplicationLicense,
    flowItemService: FlowItemService<
      BeaconWeldingData.FlowConfig,
      typeof BeaconWeldingData.FlowSectionType
    >,
    flowRuleService: FlowRuleService,
    tools: Tool[],
    protected machine: Machine
  ) {
    super(
      tools,
      flowItemService,
      flowRuleService,
      BeaconWeldingData.FlowValidationRules,
      machine
    );
  }

  public getDefaultFlowConfig(): BeaconWeldingData.FlowConfig {
    const licenseFormValues = this.getLicense();

    const availableTorches = this.getToolsForAssignedEquipment();

    if (availableTorches.length === 0) {
      logger.error('no torches found', {
        machine: this.machine,
        licenseFormValues,
      });
    }

    const defaultTorch = availableTorches[0] ?? {};

    const equipmentConfig = this.getDefaultFlowConfigCustomizations();

    const defaultConfig = {
      arcOn: true,
      home: {
        waypoint: {
          x: 0.174,
          y: -0.692,
          z: 0.253,
          rx: 1.762,
          ry: 1.762,
          rz: 0.725,
        },
        qnear: [1.57093, -1.57093, 1.57093, -1.57093, -1.57093, 1.57093],
      },
      ioMapping: this.getDefaultIOMapping(),
      torch: {
        ...defaultTorch.config,
      },
      materialType: BeaconWeldingData.Material.STEEL,
      wireDiameter: BeaconWeldingData.WireSize['035'],
      wireType: BeaconWeldingData.WireType.SOLID,
      materialThickness: BeaconWeldingData.MaterialThickness['1_8'],
      gas: BeaconWeldingData.Gas.GAS_90A_10CO2,
      betweenSpeed: 0.25,
      gasFlowRate: 35,
      arcDetectionFailureTime: 2,
      welder: {
        model: licenseFormValues.welder,
        ioMap: {
          mode: 'DO5',
          trigger: 'DO4',
        },
      },
      weave: {
        disableCornerRatioCheck: false,
      },
    };

    const merged = merge({}, defaultConfig, equipmentConfig);

    return cloneDeep(merged);
  }

  public getEquipmentName(): string {
    return this.getLicense().welder;
  }

  public preSave(
    flow: Flow<Record<string, unknown>>
  ): Promise<Flow<Record<string, unknown>>> {
    /**
     * TODO - remove when all clients are on shared code https://github.com/Hirebotics/beacon/pull/1388
     *
     * We will always copy to weldParameterSet to ensure
     * old clients have access to the data.
     *
     * Since this only exists on the new client the
     * defaultParameterSet will always be the updated field.
     */
    flow.weldParameterSet = flow.defaultParameterSet;

    return super.preSave(flow);
  }

  public async isFlowValidForTeaching(
    flow: Flow<Record<string, unknown>>
  ): Promise<boolean> {
    const configValid = flow.config !== undefined;
    const applicationDefaultParameterSet =
      flow.defaultParameterSet?.data !== undefined;

    return configValid && applicationDefaultParameterSet;
  }

  public getAvailableFlowItemParameterRefs(): FlowItemParameterRef[] {
    return Object.values(BeaconWeldingData.FlowItemParameterRef);
  }

  public getApplicationDefaultParameterSetTagToConfig(): Record<
    string,
    string
  > {
    return BeaconWeldingData.ApplicationDefaultParameterSetTagToConfig;
  }

  public getApplicationDefaultParameterSetTagToEquipmentMapping(): ApplicationDefaultParameterSetTagToEquipmentMapping {
    return BeaconWeldingData.WelderApplicationDefaultParameterSetTagEquipmentMapping;
  }

  public getApplicationDefaultParameterSetImplicitTags(
    _item: FlowItem<string>
  ): Record<string, unknown> {
    return {
      [BeaconWeldingData.ApplicationDefaultParameterSetTag.WELDER]:
        this.getEquipmentName(),
    };
  }

  public getApplicationDefaultParameterSetSubheadingFromBucket(
    bucket: ScratchpadBucket
  ): string {
    const subheadingParts: string[] = [];

    const tagMapping =
      this.getApplicationDefaultParameterSetTagToEquipmentMapping();
    const defaultTags = tagMapping[this.getEquipmentName()];
    const savedTags = bucket.tags;

    const parameterSetData = bucket.data as ApplicationDefaultParameterSetData;

    const item: FlowItem = {
      ref: BeaconWeldingData.FlowItemRef.WELD,
      parameters: parameterSetData.parameters,
    };

    const process = this.flowItemService.getFlowItemPickerLabel(
      item,
      BeaconWeldingData.FlowItemParameterRef.PROCESS
    );

    if (process) {
      subheadingParts.push(process);
    }

    const tagValues: string[] = Object.entries(savedTags).reduce(
      (vals, [name, val]) => {
        if (name in defaultTags) {
          const label = defaultTags[name].options.find((o) => o.value === val)
            ?.label;

          if (label) {
            const localized = getLocalizedText(label);

            if (localized) {
              vals.push(localized);
            }
          }
        }

        return vals;
      },
      [] as string[]
    );

    if (tagValues.length > 0) {
      subheadingParts.push(...tagValues);
    }

    if (tagValues.length) {
      subheadingParts.push(...tagValues);
    }

    return subheadingParts.join(', ');
  }

  protected getLicense(): BeaconWeldingData.License {
    return this.license.licenseFormValues as BeaconWeldingData.License;
  }

  protected getDefaultFlowConfigCustomizations(): Partial<BeaconWeldingData.FlowConfig> {
    const equipment = this.getEquipmentName();

    let config: Partial<BeaconWeldingData.FlowConfig> = {};

    // helper merge function that allows us to merge partials without needing
    // to redefine the generic types
    const mergeConfig = (update: DeepPartial<BeaconWeldingData.FlowConfig>) => {
      config = merge<
        Partial<BeaconWeldingData.FlowConfig>,
        DeepPartial<BeaconWeldingData.FlowConfig>
      >(config, update);
    };

    const machineModel = this.machine.state?.runtimeInfo?.model;

    if (machineModel) {
      if (machineModel === URModel.UR5) {
        mergeConfig({
          home: {
            waypoint: {
              x: 0.1287,
              y: -0.4937,
              z: 0.0629,
              rx: 1.768,
              ry: 1.764,
              rz: 0.717,
            },
            qnear: [1.57, -1.57, 1.57, -1.57, -1.57, 1.57],
          },
        });
      }
    } else {
      logger.warn('no machine model found', { machine: this.machine });
    }

    if (equipment === BeaconWeldingData.Welder.ESAB_ARISTO) {
      mergeConfig({
        wireDiameter: BeaconWeldingData.WireSize['045'],
      });
    } else if (equipment === BeaconWeldingData.Welder.MILLER_DELTAWELD) {
      mergeConfig({
        welder: {
          customFields: BeaconWeldingData.MillerDeltaWeldConfigCustomFields,
        },
      });
    }

    return config;
  }
}

export { WeldingFlowServiceImpl };
